import { useEffect, useCallback, useMemo, useState } from "react";
import { 
  Box, 
  Stack,
  Typography,
} from "@mui/material";
import { usePageMetadata } from "../../hooks/usePageMetadata";
import { TopBar } from "../../components/TopBar";
import { useApiRequest } from "../../hooks/useApiRequest";
import { LoadingOverlay } from "../../components/LoadingOverlay";
import { Home } from "../../types/home";
import { GridColDef } from "@mui/x-data-grid";
import { Table } from "../../components/Table";
import { formatAsCurrency, formatWithCommas } from "../../utils/number";
import { Controller, useForm } from "react-hook-form";
import { AutocompleteField } from "../../components/AutocompleteField";
import { Retailer } from "../../types/tenant";
import InputField from "../../components/InputField";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ImpersonationBanner } from "../../components/ImpersonationBanner";
import { useSession } from "../../hooks/useSession";
import NewQuoteModal from "../QuotesRoute/NewQuoteModal";
import {
  generatePath,
  useNavigate,
} from "react-router-dom";
import { routes } from "../../routes";

const BuyerModeHomesRoute: React.FC = () => {
  const {
    data: homeData,
    loading: isLoading,
    request: homeRequest,
  } = useApiRequest<[]>(false);

  const refreshHomes = useCallback(() => {
    homeRequest("/retailer/homes", { method: "GET" });
  }, [homeRequest]);

  useEffect(() => {
    refreshHomes();
  }, [refreshHomes]);

  const { isSessionLoaded, isImpersonating, user } = useSession();
  
  const columns: GridColDef<Home>[] = useMemo(() => [
    {
      field: "manufacturerName",
      headerName: "Manufacturer",
      flex: 1.2,
    },
    {
      field: "modelConstructionType",
      headerName: "Home Type",
      flex: 0.5,
      valueFormatter: (_, row) => row.modelConstructionType === 'm' ? "Manufactured" : "Modular"
    },
    {
      field: "modelName",
      headerName: "Model Name",
      flex: 1,
    },
    {
      field: "modelNumber",
      headerName: "Model Number",
      flex: 1,
    },        
    {
      field: "totalSpace",
      headerName: "Sq Ft",
      headerAlign: "right",
      align: "right",
      flex: 0.3,
      valueFormatter: (_, row) => formatWithCommas(row.totalSpace)
    },
    {
      field: "bedroomCount",
      headerName: "Bedrooms",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
    },
    {
      field: "bathroomCount",
      headerName: "Bathrooms",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
    },
    {
      field: "modelPurchasePrice",
      headerName: "Purchase Price",
      headerAlign: "right",
      align: "right",
      flex: 0.6,
      valueFormatter: (_, row) => formatAsCurrency(row.modelPurchasePrice)
    },
    {
      field: "attachments",
      headerName: "",
      width: 10,
      align: "center",
      sortable: false,
      renderCell: ({ row }) => {
        if (!row.modelLink) return <></>;

        return (
          <a
            href={row.modelLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <OpenInNewIcon fontSize="small" />
          </a>
        );
      },
    },
  ],[]);

  usePageMetadata({ title: "Homes & Purchase Prices" });
  
  type FormData = {
    manufacturerTenantGuid: string | null;
    homeType: string | null;
    bedrooms: number | null;
    bathrooms: number | null;
    minSqFt: number | null;
    maxSqFt: number | null;
    minPrice: number | null;
    maxPrice: number | null;
  };
  
  const getDefaultValues = (): FormData => {
    return {
      manufacturerTenantGuid: null,
      homeType: null,
      bedrooms: null,
      bathrooms: null,
      minSqFt: null,
      maxSqFt: null,
      minPrice: null,
      maxPrice: null,
    };
  };

  const {
    control,
    watch,
    register,
  } = useForm<FormData>({
    defaultValues: getDefaultValues(),
  });

  const manufacturerTenantGuid = watch("manufacturerTenantGuid");
  const homeType = watch("homeType");
  const bedrooms = watch("bedrooms");
  const bathrooms = watch("bathrooms");
  const minSqFt = watch("minSqFt");
  const maxSqFt = watch("maxSqFt");
  const minPrice = watch("minPrice");
  const maxPrice = watch("maxPrice");
  const {
    data: manufacturerData,
    loading: manufacturerLoading,
    request: manufacturerRequest,
  } = useApiRequest<Retailer[]>();

  useEffect(() => {
    manufacturerRequest("/retailer/manufacturers", {
      method: "GET",
    });
  }, [manufacturerRequest]);

  const manufacturerSelectionOptions = useMemo(() => {
    if (!manufacturerData) return [];
    return manufacturerData.map((mfg) => ({
      label: `${mfg.tenantName}`,
      citystate:
        mfg.tenantCity && mfg.tenantState
          ? `${mfg.tenantCity}, ${mfg.tenantState}`
          : "",
      value: mfg.tenantGuid,
    }));
  }, [manufacturerData]);

  const homeTypeOptions = [
    {
      label: "Modular",
      value: "d",
    },
    {
      label: "Manufactured",
      value: "m",
    },
  ];

  const homeRows = useMemo(() => {
    if (!homeData) return [];
    return homeData
      .filter((h: Home) => !manufacturerTenantGuid || h.manufacturerTenantGuid === manufacturerTenantGuid )
      .filter((h: Home) => !homeType || h.modelConstructionType === homeType)
      .filter((h: Home) => !bedrooms || h.bedroomCount >= bedrooms)
      .filter((h: Home) => !bathrooms || h.bathroomCount >= bathrooms)
      .filter((h: Home) => Number(h.totalSpace) >= (!minSqFt ? 0 : minSqFt) && Number(h.totalSpace) <= (!maxSqFt ? 999999 : maxSqFt))
      .filter((h: Home) => Number(h.modelPurchasePrice) >= (!minPrice ? 0 : minPrice) && Number(h.modelPurchasePrice) <= (!maxPrice ? 9999999 : maxPrice))
    ;
  },[homeData, manufacturerTenantGuid, homeType, bedrooms, bathrooms, minSqFt, maxSqFt, minPrice, maxPrice]);

  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();
  const [manufacturerSelectedGuid, setManufacturerSelectedGuid] = useState<string | undefined>(undefined);
  const [modelSelectedGuid, setModelSelectedGuid] = useState<string | undefined>(undefined);

  return (
    <>
      {isLoading && <LoadingOverlay />}
      {isSessionLoaded && isImpersonating && !user?.isBuyerMode && <ImpersonationBanner />}
      <TopBar />
      <Box
        component="main"
        sx={{
          display: "flex",
          justifyContent: "center",
          height: "100vh",
          padding: "10px",
          marginBottom: "20px",
        }}
      >
        <Stack direction="column" spacing={2} sx={{ width:"100%" }}>
          <Stack 
            direction="row" 
            spacing={2} 
          >
            <Controller
              control={control}
              name="manufacturerTenantGuid"
              render={({ field }) => {
                const selectedValue = manufacturerSelectionOptions.find(
                  (s) => s.value === field.value
                );

                return (
                  <Box sx={{ width:"400px", maxWidth:"400px" }}>
                    <AutocompleteField
                      {...field}
                      disabled={manufacturerLoading}
                      options={manufacturerSelectionOptions}
                      value={selectedValue ?? null}
                      placeholder="Select Manufacturer"
                      label="Manufacturer"
                      onChange={(_, value) => {
                        field.onChange(value?.value ?? null);
                      }}
                      renderOption={(props, option) => (
                        <Box p={1} component="li" {...props}>
                          <Stack
                            spacing={1}
                            direction="row"
                            alignContent="center"
                          >
                            <Typography variant="body1">
                              {option.label}
                            </Typography>
                            <Typography variant="body2" color="gray" pt={0.3}>
                              {option.citystate}
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                    />
                  </Box>
                );
              }}
            />
            <Controller
              control={control}
              name="homeType"
              render={({ field }) => {
                const selectedValue = homeTypeOptions.find(
                  (s) => s.value === field.value
                );

                return (
                  <Box sx={{ width:"190px", maxWidth:"190px" }}>
                    <AutocompleteField
                      {...field}
                      options={homeTypeOptions}
                      value={selectedValue ?? null}
                      placeholder="Home Type"
                      label="Home Type"
                      onChange={(_, value) => {
                        field.onChange(value?.value ?? null);
                      }}
                      renderOption={(props, option) => (
                        <Box p={1} component="li" {...props}>
                          <Stack
                            spacing={1}
                            direction="row"
                            alignContent="center"
                          >
                            <Typography variant="body1">
                              {option.label}
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                    />
                  </Box>
                );
              }}
            />
            <InputField
              sx={{ width:"100px", maxWidth:"100px" }}
              label="Bedrooms"
              placeholder="Beds"
              intOnly={true}
              {...register("bedrooms")}
            />
            <InputField
              sx={{ width:"100px", maxWidth:"100px" }}
              label="Bathrooms"
              placeholder="Baths"
              intOnly={true}
              {...register("bathrooms")}
            />
            <InputField
              sx={{ width:"100px", maxWidth:"100px" }}
              label="Min Sq Feet"
              placeholder="Sq Ft"
              intOnly={true}
              {...register("minSqFt")}
            />
            <InputField
              sx={{ width:"100px", maxWidth:"100px" }}
              label="Max Sq Feet"
              placeholder="Sq Ft"
              intOnly={true}
              {...register("maxSqFt")}
            />
            <InputField
              sx={{ width:"110px", maxWidth:"110px" }}
              label="Min Price"
              placeholder="Min Price"
              intOnly={true}
              {...register("minPrice")}
            />
            <InputField
              sx={{ width:"110px", maxWidth:"110px" }}
              label="Max Price"
              placeholder="Max Price"
              intOnly={true}
              {...register("maxPrice")}
            />
          </Stack>
          <Table
            sx={{ width: "100%" }}
            columns={columns}
            rows={homeRows ?? []}
            rowSelection={false}
            getRowId={(row) => row.modelGuid}
            disableColumnFilter
            disableColumnMenu
            disableColumnResize
            disableRowSelectionOnClick
            onRowClick={({ row }) => {
              setManufacturerSelectedGuid(row.manufacturerTenantGuid);
              setModelSelectedGuid(row.modelGuid);
              setIsCreating(true);
            }}
          />
          </Stack>
      </Box>
      {isCreating && (
        <NewQuoteModal
          isOpen={isCreating}
          onClose={() => setIsCreating(false)}
          modelGuid={modelSelectedGuid}
          manufacturerTenantGuid={manufacturerSelectedGuid}
          onSaveSuccessful={(order) => {
            setIsCreating(false);
            navigate(
              generatePath(routes.quotesAndOrdersDetail.path, {
                tenantGuid: user?.tenantGuid ?? "",
                orderGuid: order.orderGuid,
              })
            );
          }}
        />
      )}
    </>
  );
};

export default BuyerModeHomesRoute;
